import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import Spinner from "../../Spinner/Spinner";
import { formatMonthDay } from "../../../utils/helpers/dateDisplay";
import { scoreHex, scoreTextClass } from "../../../utils/helpers/scoreColors";
import {
  answerTo,
  anyAnswerTo,
  isNotScored,
  lessonStatusOn,
  notScoredTitle,
  standardScopeLabel,
} from "../../../utils/helpers/standardScope";

// slate-200 — segment color for a question never answered in the counted window.
const NO_DATA_SEGMENT_HEX = "#e2e8f0";

// `t` is threaded in rather than read from a hook so this stays a plain
// function; every call site is already inside a component that has it.
const questionLabel = (q, t) => q.short_label || q.code || t("common.questionShort", { order: q.order });

function ResultBadge({ status }) {
  const { t } = useTranslation();
  if (isNotScored(status)) {
    // The lesson is not scored on this standard: nothing to observe, unlike
    // "—"; the title says why.
    return (
      <span
        className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-md border border-dashed border-slate-200 text-slate-300 text-[9px] font-bold"
        title={notScoredTitle(t, status)}
      >
        –
      </span>
    );
  }
  if (status === "pass") {
    return (
      <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
        {t("common.yes")}
      </span>
    );
  }
  if (status === "fail") {
    return (
      <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-rose-100 text-rose-700 text-[10px] font-bold uppercase tracking-wider">
        {t("common.no")}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[9px] font-bold uppercase tracking-wider"
      title={t("common.notObserved")}
    >
      —
    </span>
  );
}

// Pass share (0-100) of one question across the COUNTED observation columns
// scored on its standard (`lessonStatus`); not-observed answers count toward
// neither side. Returns null when the question was never answered there.
const questionPassShare = (question, columns, classifyAnswer) => {
  let pass = 0;
  let answered = 0;
  columns.forEach(({ detail, isCounted, lessonStatus }) => {
    if (!isCounted || lessonStatus !== "scored") return;
    const status = classifyAnswer(answerTo(detail, question));
    if (status === "pass") {
      pass += 1;
      answered += 1;
    } else if (status === "fail") {
      answered += 1;
    }
  });
  return answered ? { share: (pass / answered) * 100, pass, answered } : null;
};

// One rubric standard: collapsible card with the windowed average score, a
// per-question segment bar (each segment colored by that question's pass share
// across the counted window), and an expanded question × observation matrix.
// `columns` is [{ detail, isCounted }] oldest → newest; stale (not counted)
// columns render dimmed so they read as history, not score input.
// `versionUuid` is the rubric version `std` belongs to.
export default function StandardCard({
  std,
  versionUuid,
  score,
  isExpanded,
  onToggle,
  columns,
  classifyAnswer,
  isDetailsLoading,
}) {
  const { t } = useTranslation();

  const orderedQuestions = (std.questions || [])
    .filter((q) => !q.parent_question)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const matchedAny = anyAnswerTo(
    columns.map(({ detail }) => detail),
    std,
    orderedQuestions,
  );

  // Each lesson's status on this standard, read once for every question.
  const statusColumns = columns.map((c) => ({
    ...c,
    lessonStatus: lessonStatusOn(c.detail, std, versionUuid),
  }));

  const staleTitle = t("teacherModal.notCounted");
  const scopeLabel = standardScopeLabel(t, std);

  return (
    <div
      className={`bg-white border rounded-2xl overflow-hidden ${
        isExpanded
          ? "border-indigo-200 shadow-lg"
          : "border-slate-200 hover:shadow-md"
      }`}
    >
      <button type="button" onClick={onToggle} className="w-full text-left p-5">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-4 min-w-0">
            <span className="w-8 h-8 bg-slate-50 text-slate-500 text-[10px] font-black rounded-lg flex items-center justify-center border border-slate-100 shrink-0">
              S{std.number}
            </span>
            <div className="min-w-0">
              <h4
                className="text-sm font-bold text-slate-800 truncate"
                title={std.title}
              >
                {std.title}
              </h4>
              {scopeLabel && (
                <p className="text-[10px] font-semibold text-indigo-600 truncate">
                  {scopeLabel}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {isDetailsLoading ? (
              <span className="h-5 w-12 rounded-md bg-slate-100 animate-pulse" />
            ) : (
              <span
                className={`text-lg font-black ${score == null ? "text-slate-300" : scoreTextClass(score)}`}
              >
                {score == null ? "—" : `${score}%`}
              </span>
            )}
            <svg
              className={`w-5 h-5 text-slate-300 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        {/* One segment per question, colored by its pass share over the
            counted window — a per-criterion fingerprint of the standard. */}
        {isDetailsLoading ? (
          <div className="h-1.5 w-full rounded-full bg-slate-100 animate-pulse" />
        ) : orderedQuestions.length > 0 ? (
          <div className="flex gap-1">
            {orderedQuestions.map((q) => {
              const outcome = questionPassShare(q, statusColumns, classifyAnswer);
              return (
                <div
                  key={q.uuid}
                  className="flex-1 h-1.5 rounded-full"
                  title={
                    outcome
                      ? `${questionLabel(q, t)} · ${outcome.pass}/${outcome.answered}`
                      : `${questionLabel(q, t)} · ${t("common.notObserved")}`
                  }
                  style={{
                    backgroundColor: outcome
                      ? scoreHex(outcome.share)
                      : NO_DATA_SEGMENT_HEX,
                  }}
                />
              );
            })}
          </div>
        ) : (
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            {score != null && (
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${score}%`, backgroundColor: scoreHex(score) }}
              />
            )}
          </div>
        )}
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="p-5 pt-0 border-t border-slate-100 bg-slate-50/40">
              {isDetailsLoading ? (
                <Spinner
                  label={t("teacherModal.loadingObservation")}
                  size="sm"
                  className="py-6"
                />
              ) : columns.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">
                  {t("teacherModal.noDetail")}
                </div>
              ) : orderedQuestions.length > 0 && !matchedAny ? (
                // None of the recent observations carry answers for this
                // standard: lessons in other subjects, or an older rubric.
                <div className="py-6 text-center text-xs text-slate-400 pt-4">
                  {scopeLabel ? (
                    t("subjectScope.notScoredRecently")
                  ) : (
                    <>
                      {t("teacherModal.noMatchingAnswers")}
                      <br />
                      {t("teacherModal.earlierRubric")}
                    </>
                  )}
                </div>
              ) : (
                <div className="pt-4 overflow-x-auto">
                  <div className="flex items-center gap-3 mb-2 px-2 min-w-fit">
                    <span className="flex-1 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                      {t("teacherModal.criteria")}
                    </span>
                    <div className="flex gap-3 shrink-0">
                      {columns.map(({ detail, isCounted }, i) => (
                        <div
                          key={detail.uuid}
                          className={`w-16 text-center ${isCounted ? "" : "opacity-40"}`}
                          title={isCounted ? undefined : staleTitle}
                        >
                          <span className="block text-[9px] font-black text-slate-500 uppercase tracking-wider">
                            {t("teacherModal.obsN", { n: i + 1 })}
                          </span>
                          <span className="block text-[9px] font-bold text-slate-500">
                            {formatMonthDay(detail.observed_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <ul className="space-y-1.5">
                    {orderedQuestions.map((q) => (
                      <li
                        key={q.uuid}
                        className="flex justify-between items-center gap-3 p-2 rounded-lg bg-white border border-slate-100 min-w-fit"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="font-mono text-[9px] font-bold bg-slate-100 text-slate-500 rounded px-1.5 py-0.5 border border-slate-200 shrink-0">
                            {questionLabel(q, t)}
                          </span>
                          <span className="text-slate-600 text-xs leading-snug">
                            {q.text}
                          </span>
                        </div>
                        <div className="flex gap-3 items-center shrink-0">
                          {statusColumns.map(({ detail, isCounted, lessonStatus }) => (
                            <div
                              key={detail.uuid}
                              className={`w-16 flex justify-center ${isCounted ? "" : "opacity-40"}`}
                              title={isCounted ? undefined : staleTitle}
                            >
                              <ResultBadge
                                status={
                                  lessonStatus === "scored"
                                    ? classifyAnswer(answerTo(detail, q))
                                    : lessonStatus
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
